import { env } from '@/env';
import type { API } from '@/server';
import { treaty } from '@elysiajs/eden';
import { useMemo } from 'react';

// Global state to track refresh attempts and prevent infinite loops
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;

// Create Eden Treaty client with proper types
export const createTreatyClient = () => {
  return treaty<API>(env().VITE_API_URL, {
    headers() {
      return {
        Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
      };
    },
    fetch: {
      credentials: 'include',
    },
    onResponse: async (response: Response): Promise<Response | undefined> => {
      if (response.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken');

        // If no refresh token, show login dialog immediately
        if (!refreshToken) {
          showLoginDialog();
          return;
        }

        // If we've tried too many times, show login dialog
        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          refreshAttempts = 0;
          showLoginDialog();
          return;
        }

        try {
          refreshAttempts++;
          const client = createTreatyClient();
          const refreshResponse = await client.api.auth.refresh.post({
            refreshToken,
          });

          if (refreshResponse.data && refreshResponse.status === 200) {
            const { accessToken } = refreshResponse.data.data;
            localStorage.setItem('accessToken', accessToken);

            // Retry the original request with new token
            const res = await fetch(response.url, {
              ...response,
              headers: {
                ...response.headers,
                Authorization: `Bearer ${accessToken}`,
              },
            });

            return await res.json();
          }
          console.error('Token refresh failed:', refreshResponse.error?.value);
          showLoginDialog();
        } catch (error) {
          console.error('Token refresh failed:', error);
          showLoginDialog();
        }
      }
    },
  });
};

// Show login dialog by dispatching a custom event
const showLoginDialog = () => {
  // localStorage.removeItem('accessToken');
  // localStorage.removeItem('refreshToken');
  window.dispatchEvent(new CustomEvent('show-login-dialog'));
};

// For React components
export const useTreatyClient = () => {
  const client = useMemo(() => createTreatyClient(), []);

  return client;
};
